// OrgTreeTab — data-loading host for the top-down org tree. Used by both
// `/admin/people` and the all-users `/people` page.
//
// Owns nothing beyond fetch/loading/error/empty state — the rendering and
// (future) interaction live in `<OrgTree>` (the composition root). Mirrors
// the existing `<OrgGraphTab>` since both views consume the same endpoint
// (`GET /api/people/org-graph`) and the same `IOrgGraphResponse` payload.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { IOrgGraphResponse } from '@skystone/shared';
import { orgGraphApi } from '@/services/org-graph-api';
import { getErrorMessage } from '@/lib/utils';
import { ErrorBanner, PageLoading } from '@/components/ui';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { OrgTree } from '@/components/org-tree/OrgTree';

export function OrgTreeTab() {
  const [data, setData] = useState<IOrgGraphResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Monotonically-increasing request id so out-of-order responses don't
  // overwrite a newer one. Without this, two `load()` calls (StrictMode
  // double-effect, repeated Retry clicks, or a slow first request followed
  // by a fast retry) can land in reverse order — an older failure would
  // clobber newer fresh data with a stale error. See Rabizard feedback on
  // PRODOPSAI-467.
  const requestIdRef = useRef(0);

  const load = useCallback(async (): Promise<void> => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const response = await orgGraphApi.get();
      if (requestId !== requestIdRef.current) return; // stale — newer load() in flight
      setData(response);
    } catch (err: unknown) {
      if (requestId !== requestIdRef.current) return; // stale — newer load() in flight
      setError(getErrorMessage(err, 'Failed to load org tree'));
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading && data === null) {
    return <PageLoading />;
  }

  if (error !== null) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Org tree</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <ErrorBanner message={error} />
          <button
            type="button"
            onClick={load}
            className="rounded-md border border-border px-3 py-1 text-sm hover:bg-muted"
          >
            Retry
          </button>
        </CardContent>
      </Card>
    );
  }

  if (data === null || data.nodes.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Org tree</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="py-8 text-center text-sm text-muted-foreground">
            No directory data available. The Entra sync may not have run yet.
          </p>
        </CardContent>
      </Card>
    );
  }

  return <OrgTree data={data} />;
}
